/**
 * Pushes GA4-style ecommerce events to window.dataLayer for browse -> cart -> begin_checkout.
 * Hooks into the theme's existing PUB_SUB_EVENTS bus (pubsub.js) instead of editing every
 * cart/product form. Purchase/order events are NOT handled here — Shopify's hosted checkout
 * is outside the theme, so those must come from a Web Pixel / Customer Events integration.
 */
(function () {
  window.dataLayer = window.dataLayer || [];

  function dlPush(event, ecommerce) {
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push({ event: event, ecommerce: ecommerce });
  }

  function currency() {
    return (window.Shopify && Shopify.currency && Shopify.currency.active) || 'USD';
  }

  function money(cents) {
    return Math.round(cents) / 100;
  }

  function sum(items) {
    return items.reduce(function (total, item) {
      return total + item.price * item.quantity;
    }, 0);
  }

  // Shopify cart line item (from /cart.js, /cart/change.js) or add.js response -> GA4 item
  function lineToItem(line, index) {
    return {
      item_id: line.sku || String(line.variant_id || line.id),
      item_name: line.product_title || line.title,
      item_variant: line.variant_title || undefined,
      item_brand: line.vendor || undefined,
      price: money(line.price),
      quantity: line.quantity,
      index: index,
    };
  }

  // ---------------------------------------------------------------------
  // Cart snapshot: kept in sync so add_to_cart/remove_from_cart/view_cart/
  // begin_checkout always have full item data, even without a fresh fetch.
  // ---------------------------------------------------------------------
  var cartSnapshot = {};

  function snapshotFromCart(cart) {
    var next = {};
    (cart.items || []).forEach(function (line) {
      next[line.key] = line;
    });
    return next;
  }

  if (window.__cartInitial) {
    cartSnapshot = snapshotFromCart(window.__cartInitial);
  }

  function diffAndPushCartChanges(newCart) {
    var newSnapshot = snapshotFromCart(newCart);
    var added = [];
    var removed = [];

    Object.keys(newSnapshot).forEach(function (key) {
      var newLine = newSnapshot[key];
      var oldQty = cartSnapshot[key] ? cartSnapshot[key].quantity : 0;
      var delta = newLine.quantity - oldQty;
      if (delta > 0) {
        added.push(lineToItem(Object.assign({}, newLine, { quantity: delta }), added.length));
      } else if (delta < 0) {
        removed.push(lineToItem(Object.assign({}, newLine, { quantity: -delta }), removed.length));
      }
    });

    Object.keys(cartSnapshot).forEach(function (key) {
      if (!newSnapshot[key]) {
        removed.push(lineToItem(cartSnapshot[key], removed.length));
      }
    });

    if (added.length) dlPush('add_to_cart', { currency: currency(), value: sum(added), items: added });
    if (removed.length) dlPush('remove_from_cart', { currency: currency(), value: sum(removed), items: removed });

    cartSnapshot = newSnapshot;
  }

  function pushAddFromLine(line) {
    var item = lineToItem(line, 0);
    dlPush('add_to_cart', { currency: currency(), value: item.price * item.quantity, items: [item] });

    var existing = cartSnapshot[line.key];
    cartSnapshot[line.key] = Object.assign({}, existing, line, {
      quantity: (existing ? existing.quantity : 0) + line.quantity,
    });
  }

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
      if (!event || !event.cart) return;

      if (event.source === 'product-form') {
        // /cart/add.js response is the added line item itself, not the full cart
        pushAddFromLine(event.cart);
      } else if (event.cart.items) {
        // /cart/change.js and /cart/update.js responses are the full cart
        diffAndPushCartChanges(event.cart);
      }
    });
  }

  // ---------------------------------------------------------------------
  // view_item (product page) + re-fire on variant swap
  // ---------------------------------------------------------------------
  var currentProduct = null;

  function variantToItem(product, variant) {
    return {
      item_id: (variant && variant.sku) || String((variant && variant.id) || product.id),
      item_name: product.title,
      item_variant: variant ? variant.title : undefined,
      item_brand: product.vendor || undefined,
      item_category: product.type || undefined,
      price: money((variant && variant.price) || product.price),
      quantity: 1,
    };
  }

  function pushViewItem(product, variant) {
    if (!product) return;
    currentProduct = product;
    var item = variantToItem(product, variant);
    dlPush('view_item', { currency: currency(), value: item.price, items: [item] });
  }

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.variantChange, function (event) {
      var variant = event && event.data && event.data.variant;
      if (!variant || !currentProduct) return;
      pushViewItem(currentProduct, variant);
    });
  }

  // ---------------------------------------------------------------------
  // view_item_list / select_item — driven by data-ga-* attributes on
  // .card-wrapper elements (see snippets/card-product.liquid). Works for
  // any grid (collection, search, related, bundles) without editing each
  // section, and re-scans DOM changes so AJAX-swapped grids stay tracked.
  // ---------------------------------------------------------------------
  var pushedCardKeys = {};
  var pendingByList = {};
  var flushTimer = null;

  function cardElementToItem(el, index) {
    return {
      item_id: el.dataset.gaId,
      item_name: el.dataset.gaName,
      item_variant: el.dataset.gaVariant || undefined,
      item_brand: el.dataset.gaBrand || undefined,
      item_category: el.dataset.gaCategory || undefined,
      item_list_id: el.dataset.gaListId,
      item_list_name: el.dataset.gaListName,
      price: parseFloat(el.dataset.gaPrice || '0'),
      quantity: 1,
      index: index,
    };
  }

  function flushPendingLists() {
    Object.keys(pendingByList).forEach(function (listKey) {
      var entries = pendingByList[listKey];
      if (!entries.length) return;
      dlPush('view_item_list', {
        item_list_id: entries[0].item_list_id,
        item_list_name: entries[0].item_list_name,
        items: entries,
      });
    });
    pendingByList = {};
  }

  var cardObserver =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          function (entries) {
            entries.forEach(function (entry) {
              if (!entry.isIntersecting) return;
              var el = entry.target;
              cardObserver.unobserve(el);

              var cardKey = el.dataset.gaId + ':' + el.dataset.gaListId;
              if (pushedCardKeys[cardKey]) return;
              pushedCardKeys[cardKey] = true;

              var listKey = el.dataset.gaListId || 'default';
              pendingByList[listKey] = pendingByList[listKey] || [];
              pendingByList[listKey].push(cardElementToItem(el, pendingByList[listKey].length));

              clearTimeout(flushTimer);
              flushTimer = setTimeout(flushPendingLists, 400);
            });
          },
          { threshold: 0.5 }
        )
      : null;

  function observeCards(root) {
    if (!cardObserver) return;
    (root || document).querySelectorAll('.card-wrapper[data-ga-id]').forEach(function (el) {
      cardObserver.observe(el);
    });
  }

  document.addEventListener('click', function (event) {
    var wrapper = event.target.closest && event.target.closest('.card-wrapper[data-ga-id]');
    if (!wrapper) return;
    if (!event.target.closest('a')) return;

    dlPush('select_item', {
      item_list_id: wrapper.dataset.gaListId,
      item_list_name: wrapper.dataset.gaListName,
      items: [cardElementToItem(wrapper, 0)],
    });
  });

  // Re-scan for cards added after initial load (filters, infinite scroll, quick search, quick add)
  if ('MutationObserver' in window) {
    new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        mutation.addedNodes.forEach(function (node) {
          if (node.nodeType !== 1) return;
          if (node.matches && node.matches('.card-wrapper[data-ga-id]')) observeCards(node.parentNode);
          else if (node.querySelectorAll) observeCards(node);
        });
      });
    }).observe(document.body, { childList: true, subtree: true });
  }

  // ---------------------------------------------------------------------
  // view_cart / begin_checkout
  // ---------------------------------------------------------------------
  function cartItemsFromSnapshot() {
    return Object.keys(cartSnapshot).map(function (key, index) {
      return lineToItem(cartSnapshot[key], index);
    });
  }

  function pushViewCart() {
    var items = cartItemsFromSnapshot();
    dlPush('view_cart', { currency: currency(), value: sum(items), items: items });
  }

  function pushBeginCheckout() {
    var items = cartItemsFromSnapshot();
    dlPush('begin_checkout', { currency: currency(), value: sum(items), items: items });
  }

  document.addEventListener('click', function (event) {
    if (event.target.closest && event.target.closest('button[name="checkout"]')) {
      pushBeginCheckout();
    }
  });

  function initCartDrawerListener() {
    var details = document.querySelector('cart-drawer details');
    if (!details) return;
    details.addEventListener('toggle', function () {
      if (details.hasAttribute('open')) pushViewCart();
    });
  }

  window.themeAnalytics = {
    viewItem: pushViewItem,
  };

  document.addEventListener('DOMContentLoaded', function () {
    observeCards(document);
    initCartDrawerListener();
  });
})();
